import {
  SkyButton,
  SkyDialog,
  SkyMessageBar,
  SkySkeleton,
  SkyText,
} from "@skypointcloud/skypoint-ui";
import { useMutation } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import type { TechnicalDetailsResponse } from "@shared/types/technicalDetails";
import { apiRequest, failureMessage } from "@/lib/api";
import { InlineErrorBar, useInlineError } from "@/lib/feedback/InlineErrorBar";
import { DONE_LABEL_MS } from "@/lib/feedback/NoticeRegion";
import { usePermissions } from "@/hooks/use-permissions";
import { runPath } from "@/hooks/use-runs";

/**
 * "Technical details" (round 10 wave 2, R2.4, design §1.4): the raw text behind one run event,
 * which its row only fingerprints (SEC-17). For holders of `instance.audit.read` only; the server
 * refuses anyone else and audits every attempt.
 *
 * The text may hold personal or health information, so it is read once per open and kept nowhere:
 * a `useMutation` (never cached, never refetched), shown in this dialog only, dropped when it
 * closes. The request names the event id and nothing else. Copy confirms inside the dialog, as the
 * SQL dialog does; nothing goes to a notice or a toast.
 */

const OUTCOME_WORDS: Record<Exclude<TechnicalDetailsResponse["outcome"], "answered">, string> = {
  not_found: "No log lines were found for this event around the time it was written.",
  expired: "The logs for this event are older than the log retention, so they are no longer kept.",
  unavailable:
    "The logs could not be read right now, or this environment does not keep them. Try again later.",
};

const WARNING =
  "This is the raw text the import logged. It can contain personal or health information. Copy it only where such information may be kept, and do not paste it into tickets or chat.";

/** One technical details read: the event id only. Never cached. */
function useTechnicalDetails(runId: string) {
  return useMutation({
    mutationFn: (eventId: string) =>
      apiRequest<TechnicalDetailsResponse>(`${runPath(runId)}/technical-details`, {
        method: "POST",
        body: { eventId },
      }),
    gcTime: 0,
  });
}

export interface TechnicalDetailsDialogProps {
  runId: string;
  eventId: string;
  onClose: () => void;
}

/** Mounted by its button only while open, so each open is one read. */
export function TechnicalDetailsDialog({ runId, eventId, onClose }: TechnicalDetailsDialogProps) {
  const details = useTechnicalDetails(runId);
  const asked = useRef(false);
  const [copied, setCopied] = useState(false);
  const copyError = useInlineError();
  const { mutate } = details;

  useEffect(() => {
    // Once per open, StrictMode's second effect run included: every read is an audited view.
    if (asked.current) return;
    asked.current = true;
    mutate(eventId);
  }, [mutate, eventId]);

  useEffect(() => {
    if (!copied) return;
    const handle = setTimeout(() => setCopied(false), DONE_LABEL_MS);
    return () => clearTimeout(handle);
  }, [copied]);

  const data = details.data;
  const text = data?.outcome === "answered" ? data.text : null;

  const copy = async (): Promise<void> => {
    if (!text) return;
    copyError.clear();
    setCopied(false);
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
    } catch (error) {
      copyError.show(error, "The text could not be copied. Select it and copy it instead.");
    }
  };

  return (
    <SkyDialog
      open
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
      title="Technical details"
      description={`Run ${runId}, event ${eventId}. Viewing this is recorded in the audit trail.`}
      size="large"
      testId="technical-details-dialog"
      secondaryAction={{ label: "Close", onClick: onClose }}
    >
      <div className="flex flex-col gap-3">
        <SkyMessageBar
          intent="warning"
          layout="multiline"
          title="May contain personal or health information"
          message={WARNING}
          testId="technical-details-warning"
        />
        <InlineErrorBar
          error={copyError.error}
          title="The text could not be copied"
          testId="technical-details-copy-error"
        />

        {details.isPending ? (
          <SkySkeleton testId="technical-details-loading" aria-busy="true">
            <div className="h-40 rounded-md bg-muted" />
          </SkySkeleton>
        ) : null}

        {details.isError ? (
          <SkyMessageBar
            intent="error"
            layout="multiline"
            title="The technical details could not be read"
            message={failureMessage(details.error, "Try again.")}
            actions={
              <SkyButton
                size="small"
                onClick={() => mutate(eventId)}
                testId="technical-details-retry"
              >
                Try again
              </SkyButton>
            }
            testId="technical-details-error"
          />
        ) : null}

        {data && data.outcome !== "answered" ? (
          <SkyMessageBar
            intent="info"
            layout="multiline"
            message={OUTCOME_WORDS[data.outcome]}
            testId={`technical-details-${data.outcome}`}
          />
        ) : null}

        {text !== null ? (
          <>
            <div className="flex items-center justify-between gap-2">
              <SkyText size={200} color="muted" aria-live="polite">
                {copied
                  ? "Text copied."
                  : data?.truncated
                    ? "The text was longer than 32 KB and has been cut."
                    : `${(data?.chars ?? 0).toLocaleString("en-US")} characters`}
              </SkyText>
              <SkyButton
                size="small"
                appearance="secondary"
                onClick={() => void copy()}
                testId="technical-details-copy"
              >
                {copied ? "Copied" : "Copy"}
              </SkyButton>
            </div>
            <pre
              className="max-h-96 overflow-auto whitespace-pre-wrap break-words rounded-md bg-muted p-3 font-mono text-xs"
              tabIndex={0}
              aria-label="Technical details text"
              data-testid="technical-details-text"
            >
              {text}
            </pre>
          </>
        ) : null}
      </div>
    </SkyDialog>
  );
}

export interface TechnicalDetailsButtonProps {
  runId: string;
  eventId: string;
  testId?: string;
}

/** The button and its dialog. Renders nothing without `instance.audit.read`. */
export function TechnicalDetailsButton({ runId, eventId, testId }: TechnicalDetailsButtonProps) {
  const { canAny } = usePermissions();
  const [open, setOpen] = useState(false);
  if (!canAny("instance.audit.read")) return null;
  return (
    <>
      <SkyButton
        type="button"
        appearance="subtle"
        size="small"
        onClick={() => setOpen(true)}
        testId={testId ?? "technical-details-open"}
      >
        Technical details
      </SkyButton>
      {open ? (
        <TechnicalDetailsDialog runId={runId} eventId={eventId} onClose={() => setOpen(false)} />
      ) : null}
    </>
  );
}

/** True for an event whose raw text Log Analytics may hold: the server offers it the same way. */
export function hasTechnicalHandle(payload: unknown): boolean {
  if (!payload || typeof payload !== "object") return false;
  const row = payload as Record<string, unknown>;
  const containerLog = row.rawErrorIn === "container-logs";
  // A connector's error by its execution's console; the runner's own error by its fingerprint.
  if (containerLog) return typeof row.executionName === "string";
  return typeof row.errorFingerprint === "string";
}
